/**
 * ProcessSection — "Our Process" content for the bridal beauty journey.
 * Renders a winding vertical roadmap with 6 steps, each with icon, custom SVG illustration, and copy.
 * Uses Framer Motion for scroll-triggered animations; steps alternate left/right on desktop.
 */
import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, FileText, Palette, Sparkles, UserCheck, Heart } from "lucide-react";

// SVG Illustrations for each step (bridal/Indian-inspired: palace, mirror, palette, etc.)
const StepIllustrations = {
  step01: (
    <svg viewBox="0 0 120 100" className="w-full h-auto">
      {/* Isometric Indian Palace/Temple */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        {/* Main dome */}
        <ellipse cx="60" cy="20" rx="25" ry="8" />
        <path d="M 35 20 L 60 5 L 85 20" />
        {/* Side domes */}
        <ellipse cx="30" cy="25" rx="12" ry="5" />
        <ellipse cx="90" cy="25" rx="12" ry="5" />
        <path d="M 18 25 L 30 18 L 42 25" />
        <path d="M 78 25 L 90 18 L 102 25" />
        {/* Base structure */}
        <rect x="20" y="30" width="80" height="50" />
        <rect x="25" y="35" width="70" height="40" />
        {/* Arches */}
        <path d="M 30 50 Q 30 40, 40 40 Q 50 40, 50 50" />
        <path d="M 70 50 Q 70 40, 80 40 Q 90 40, 90 50" />
      </g>
    </svg>
  ),
  step02: (
    <svg viewBox="0 0 120 100" className="w-full h-auto">
      {/* Vanity Mirror */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <ellipse cx="50" cy="40" rx="35" ry="25" />
        <ellipse cx="50" cy="40" rx="28" ry="20" />
        <ellipse cx="50" cy="40" rx="22" ry="15" />
        {/* Mirror handle */}
        <rect x="48" y="60" width="4" height="15" />
      </g>
      {/* Makeup Palette */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <rect x="75" y="50" width="35" height="25" rx="2" />
        <circle cx="82" cy="58" r="4" />
        <circle cx="95" cy="58" r="4" />
        <circle cx="82" cy="67" r="4" />
        <circle cx="95" cy="67" r="4" />
      </g>
    </svg>
  ),
  step03: (
    <svg viewBox="0 0 120 100" className="w-full h-auto">
      {/* Indian Palace */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <ellipse cx="50" cy="20" rx="20" ry="7" />
        <path d="M 30 20 L 50 8 L 70 20" />
        <rect x="25" y="25" width="50" height="40" />
        <rect x="30" y="30" width="40" height="30" />
        <path d="M 35 45 Q 35 38, 42 38 Q 49 38, 49 45" />
        <path d="M 55 45 Q 55 38, 62 38 Q 69 38, 69 45" />
      </g>
      {/* Lipstick Tube */}
      <g stroke="#622935" strokeWidth="1.5" fill="none">
        <rect x="80" y="30" width="12" height="35" rx="1" />
        <rect x="82" y="32" width="8" height="30" fill="#622935" opacity="0.3" />
        <ellipse cx="86" cy="30" rx="6" ry="2" fill="#622935" />
      </g>
    </svg>
  ),
  step04: (
    <svg viewBox="0 0 120 100" className="w-full h-auto">
      {/* Traditional Indian Building */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <rect x="20" y="35" width="60" height="45" />
        <ellipse cx="50" cy="20" rx="18" ry="6" />
        <path d="M 32 20 L 50 10 L 68 20" />
        {/* Arches */}
        <path d="M 30 50 Q 30 42, 38 42 Q 46 42, 46 50" />
        <path d="M 54 50 Q 54 42, 62 42 Q 70 42, 70 50" />
        <rect x="25" y="40" width="50" height="35" />
      </g>
      {/* Hair Comb */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <rect x="85" y="50" width="20" height="3" />
        <line x1="87" y1="50" x2="87" y2="45" />
        <line x1="90" y1="50" x2="90" y2="45" />
        <line x1="93" y1="50" x2="93" y2="45" />
        <line x1="96" y1="50" x2="96" y2="45" />
        <line x1="99" y1="50" x2="99" y2="45" />
        <line x1="102" y1="50" x2="102" y2="45" />
      </g>
    </svg>
  ),
  step05: (
    <svg viewBox="0 0 120 100" className="w-full h-auto">
      {/* Lipstick Tube */}
      <g stroke="#622935" strokeWidth="1.5" fill="none">
        <rect x="20" y="40" width="14" height="40" rx="1" />
        <rect x="22" y="42" width="10" height="35" fill="#622935" opacity="0.3" />
        <ellipse cx="27" cy="40" rx="7" ry="2" fill="#622935" />
      </g>
      {/* Makeup Palette with Brush */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <rect x="45" y="55" width="30" height="20" rx="2" />
        <circle cx="52" cy="62" r="3" />
        <circle cx="62" cy="62" r="3" />
        <circle cx="52" cy="68" r="3" />
        <circle cx="62" cy="68" r="3" />
        {/* Brush */}
        <line x1="80" y1="50" x2="80" y2="70" />
        <ellipse cx="80" cy="48" rx="4" ry="3" fill="#8B7B7B" opacity="0.3" />
      </g>
      {/* Lotus Flower */}
      <g stroke="#622935" strokeWidth="1.5" fill="none" opacity="0.6">
        <circle cx="95" cy="60" r="6" />
        <circle cx="95" cy="60" r="4" />
      </g>
    </svg>
  ),
  step06: (
    <svg viewBox="0 0 120 100" className="w-full h-auto">
      {/* Large Makeup Brush */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <line x1="50" y1="30" x2="50" y2="70" />
        <ellipse cx="50" cy="28" rx="12" ry="8" fill="#8B7B7B" opacity="0.2" />
        <ellipse cx="50" cy="28" rx="10" ry="6" fill="#8B7B7B" opacity="0.3" />
        {/* Brush bristles */}
        <line x1="42" y1="28" x2="42" y2="25" />
        <line x1="45" y1="28" x2="45" y2="25" />
        <line x1="48" y1="28" x2="48" y2="25" />
        <line x1="52" y1="28" x2="52" y2="25" />
        <line x1="55" y1="28" x2="55" y2="25" />
        <line x1="58" y1="28" x2="58" y2="25" />
      </g>
      {/* Round Makeup Container */}
      <g stroke="#8B7B7B" strokeWidth="1.5" fill="none">
        <circle cx="80" cy="60" r="12" />
        <circle cx="80" cy="60" r="8" fill="#8B7B7B" opacity="0.2" />
        <ellipse cx="80" cy="58" rx="8" ry="6" fill="#8B7B7B" opacity="0.1" />
      </g>
    </svg>
  ),
};

const ProcessSection = () => {
  // Six-step bridal process: enquiry → questionnaire → moodboard → final look → artist assignment → wedding day
  const steps = [
    {
      number: "01",
      title: "ENQUIRY & PERSONAL CONSULTATION",
      description:
        "Your bridal journey begins with an initial enquiry. We create a customized bridal makeup and hair package tailored to your city, wedding events, and unique vision. Every consultation is designed to understand your personality and ensure a stress-free, luxury bridal experience from start to finish.",
      icon: MessageCircle,
      illustration: StepIllustrations.step01,
    },
    {
      number: "02",
      title: "BOOKING & BRIDAL QUESTIONNAIRE",
      description:
        "Once your booking is confirmed, a detailed bridal beauty questionnaire helps us learn about your skin type, hair texture, and makeup preferences. This step allows our experts to curate a bridal skincare and haircare plan that prepares you perfectly for your big day.",
      icon: FileText,
      illustration: StepIllustrations.step02,
    },
    {
      number: "03",
      title: "CONSULTATION WITH EXPERT STYLIST & MOODBOARDING",
      description:
        "Our bridal makeup artist collaborates with you to design a personalized bridal moodboard, aligning your desired look with your outfit, tone, and occasion. From South Indian bridal makeup to modern fusion looks, every detail reflects your individuality and enhances your natural glow.",
      icon: Palette,
      illustration: StepIllustrations.step03,
    },
    {
      number: "04",
      title: "FINAL LOOK DESIGN",
      description:
        "Your final bridal look design is crafted with precision and balance—combining elegance, comfort, and timeless charm. Using professional makeup techniques, our experts ensure every element complements your features while achieving long-lasting perfection.",
      icon: Sparkles,
      illustration: StepIllustrations.step04,
    },
    {
      number: "05",
      title: "ARTIST ASSIGNMENT",
      description:
        "A celebrity bridal makeup artist with over a decade of experience is exclusively assigned to you. Trained under India's top professionals, our artists use luxury bridal beauty products to deliver flawless, radiant results that last through every event and emotion.",
      icon: UserCheck,
      illustration: StepIllustrations.step05,
    },
    {
      number: "06",
      title: "THE BRIDAL DAY EXPERIENCE",
      description:
        "On your wedding day, our artist brings your moodboard to life with seamless execution. From pre-wedding prep to your final touch-up, your bridal makeup and hair styling experience is managed with artistry, precision, and calm. Walk into your celebration glowing with confidence and elegance.",
      icon: Heart,
      illustration: StepIllustrations.step06,
    },
  ];

  return (
    <section className="relative py-28 md:py-40 overflow-hidden bg-white" aria-label="Our Process">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section title and tagline */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h2 className="text-5xl md:text-6xl font-display text-wine font-bold tracking-tight mb-4">
            OUR PROCESS
          </h2>
          <p className="text-base md:text-lg text-black font-body">
            The only bridal beauty brand in India delivering a seamless, luxurious, and personalized bridal experience.
          </p>
        </div>

        {/* Winding vertical roadmap: SVG path down center with step markers and lotus dots */}
        <div className="relative flex flex-col items-center">
          {/* Decorative SVG: curved path + lotus circles between steps */}
          <svg
            className="absolute left-1/2 -translate-x-1/2 top-0 h-full w-[300px] md:w-[400px] pointer-events-none z-0"
            viewBox="0 0 400 2000"
            preserveAspectRatio="none"
            fill="none"
          >
            {/* Main winding path */}
            <path
              d="
                M 200 0 
                C 50 200, 350 400, 200 600 
                C 50 800, 350 1000, 200 1200
                C 50 1400, 350 1600, 200 1800
                C 50 1900, 350 1950, 200 2000
              "
              stroke="#622935"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Lotus flower decorations along the path */}
            <g>
              <circle cx="200" cy="150" r="8" fill="#622935" opacity="0.6" />
              <circle cx="200" cy="450" r="8" fill="#622935" opacity="0.6" />
              <circle cx="200" cy="750" r="8" fill="#622935" opacity="0.6" />
              <circle cx="200" cy="1050" r="8" fill="#622935" opacity="0.6" />
              <circle cx="200" cy="1350" r="8" fill="#622935" opacity="0.6" />
              <circle cx="200" cy="1650" r="8" fill="#622935" opacity="0.6" />
            </g>
          </svg>

          {/* Step cards: alternate left/right on desktop, centered number on path */}
          <div className="flex flex-col gap-24 md:gap-32 relative z-10">
            {steps.map((step, index) => {
              const isLeft = index % 2 === 0; // Even steps: content left; odd: content right

              return (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative flex flex-col md:flex-row items-center ${
                    isLeft ? "md:flex-row-reverse" : "md:flex-row"
                  } gap-8 md:gap-16`}
                >
                  {/* Circular step number badge (overlays the winding path) */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-20">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-wine border-4 border-white shadow-lg">
                      <span className="text-white font-display text-sm font-bold">
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Step illustration */}
                  <div className={`w-32 md:w-40 h-24 md:h-28 flex items-center justify-center ${
                    isLeft ? "md:order-2" : "md:order-1"
                  }`}>
                    {step.illustration}
                  </div>

                  {/* Title + description card (position varies by isLeft) */}
                  <div
                    className={`bg-white border border-wine/20 rounded-lg shadow-sm p-6 md:p-8 w-full md:w-[420px] hover:shadow-md transition-all duration-300 ${
                      isLeft ? "md:mr-auto text-left md:order-1" : "md:ml-auto text-left md:order-2"
                    }`}
                  >
                    <h3 className="text-base md:text-lg font-display text-wine font-bold mb-4 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-sm md:text-base text-black font-body leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

       
      </div>
    </section>
  );
};

export default ProcessSection;

